import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {Observable, of} from 'rxjs';
import {FormControl} from '@angular/forms';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {debounceTime, filter, finalize, switchMap, tap} from 'rxjs/operators';
import {FormInputComponent} from '../form-input/form-input.component';

@Component({
  selector: 'item-autocomplete',
  templateUrl: './item-autocomplete.component.html',
  styleUrls: ['./item-autocomplete.component.scss']
})
export class ItemAutocompleteComponent<T> implements OnInit {

  private static readonly MAX_SEARCH_STRING_LENGTH = 64;

  @Input() label!: string;
  @Input() placeholder?: string;

  @Input() required = true;

  @Input() fitText = false;
  @Input() clearAfterSelect = true;
  @Input() allowManualClear = false;

  @Input() displayWith!: (item?: T) => string;
  @Input() searchWith!: (query: string) => Observable<T[]>;

  @Output() selected = new EventEmitter<T>();
  @Output() cleared = new EventEmitter<void>();

  @ViewChild('search', {static: true}) searchElement!: FormInputComponent;

  queryField!: FormControl;

  dataLoading = false;
  foundItems: T[] = [];

  skipSearch = false;

  manualClear = () => {
    this.clearInput();
    this.cleared.emit();
  };

  constructor() {
  }

  ngOnInit(): void {
    this.queryField = new FormControl('', []);

    this.queryField.valueChanges
      .pipe(
        filter(() => {
          if (this.skipSearch) {
            this.skipSearch = false;
            return false;
          } else {
            return true;
          }
        }),
        debounceTime(300),
        tap(() => {
          this.dataLoading = true;
        }),
        switchMap(value => this.search(value as string)
          .pipe(
            finalize(() => this.dataLoading = false),
          )
        )
      )
      .subscribe((items: T[]) => {
        this.foundItems = items || [];
      });
  }

  optionSelected(event: MatAutocompleteSelectedEvent) {
    if (this.clearAfterSelect) {
      this.queryField.setValue(null);
    }

    this.selected.emit(event.option.value as T);
  }

  private search(query: string): Observable<T[]> {
    return query && query.length > 2
      ? this.searchWith(query.trim().substring(0, ItemAutocompleteComponent.MAX_SEARCH_STRING_LENGTH))
      : of([]);
  }

  public disable() {
    this.queryField.disable();
  }

  public enable() {
    this.queryField.enable();
  }

  public clearInput(markUntouched = false, focus = true) {
    this.queryField.setValue(undefined);

    if (markUntouched) this.queryField.markAsUntouched();
    if (focus) this.focusOnSearch();

    this.cleared.emit();
  }

  public focusOnSearch() {
    this.searchElement.setFocus();
  }

  public setValue(value: T) {
    this.skipSearch = true;
    this.queryField.setValue(value);
  }
}
